import { useState, useMemo } from 'react';
import { Pressable, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import DropdownMenu from '../DropdownMenu';
import Unicon from '../Unicon';
import InlineHelpIcon from './InlineHelpIcon';

const SelectInput = ({ value, onUpdate, title, helpText = '', placeholder = "Select an Option", options, disabled = false, hideClearButton = false, hideSelectedPills = false, multiple = false, required = false, isNotOnSurface = false, icon = null }) => {
    const [triggerLayout, setTriggerLayout] = useState(null);
    const [hoveredPill, setHoveredPill] = useState(null);

    const valueToUse = value === null || value === undefined ? null : (typeof value === 'object' ? value : {label: value, value: value});

    // For multiple mode, value is an array of values
    const selectedValues = multiple ? (Array.isArray(valueToUse) ? valueToUse : []) : [];
    const item_from_id = !multiple ? options?.find(item => item.value == valueToUse?.value) : null;
    
    const options_list = options?.map((option) => {
        let item;

        if(typeof option !== 'object'){
            item = {
                label: option,
                value: option
            }
        }
        else{
            item = option
        }

        // Separator items render as a divider in the dropdown (no value/onSelect).
        if (item.separator) {
            return { separator: true };
        }
        // Section items render as a non-selectable group header.
        if (item.section) {
            return { section: item.section };
        }

        const isSelected = multiple ? selectedValues.includes(item.value) : item.value == valueToUse?.value;

        return {
            name: item?.label,
            selected: isSelected,
            disabled: item.disabled,
            icon: item.icon,
            subtitle: item.subtitle,
            body: item.body,
            onSelect: () => {
                if(multiple) {
                    if(isSelected) {
                        onUpdate(selectedValues.filter(v => v !== item.value));
                    } else {
                        onUpdate([...selectedValues, item.value]);
                    }
                } else {
                    onUpdate(item.value);
                }
            }
        }
    });

    // Get selected items for display in multiple mode
    const selectedItems = useMemo(() => {
        if(!multiple) return [];
        return options?.filter(opt => {
            const optValue = typeof opt === 'object' ? opt.value : opt;
            return selectedValues.includes(optValue);
        }).map(opt => ({
            value: typeof opt === 'object' ? opt.value : opt,
            label: typeof opt === 'object' ? opt.label : opt
        })) || [];
    }, [multiple, options, selectedValues]);

    const pills = useMemo(() => {
        if(!multiple || selectedItems.length === 0) return null;
        return selectedItems.map(item => (
            <Pressable 
                key={item.value}
                onHoverIn={() => setHoveredPill(item.value)} 
                onHoverOut={() => setHoveredPill(null)} 
                style={styles.pill(hoveredPill, item.value)} 
                onPress={() => {
                    onUpdate(selectedValues.filter(v => v !== item.value));
                }}
            >
                <Text style={styles.pillText(hoveredPill, item.value)}>{item.label}</Text>
                <Unicon name="close" size={12} colorVariant={hoveredPill === item.value ? "onPrimary" : "text"} />
            </Pressable>
        ));
    }, [multiple, selectedItems, hoveredPill, selectedValues]);

    const displayText = useMemo(() => {
        if(multiple) {
            if(selectedItems.length === 0) return null;
            return `${selectedItems.length} selected`;
        }
        return item_from_id?.label || valueToUse?.label;
    }, [multiple, selectedItems, item_from_id, valueToUse?.label]);

    const showClose = (multiple ? selectedValues.length > 0 : valueToUse?.value) && !hideClearButton

    const allSelected = multiple && options?.length > 0 && options?.every(opt => {
        const optValue = typeof opt === 'object' ? opt.value : opt;
        return selectedValues.includes(optValue);
    });

    const toolBarButtons = multiple ? [
        {
            name: allSelected ? "Select None" : "Select All",
            icon: { name: allSelected ? "delete-sweep" : "checkmark-done-outline", size: 20, library: allSelected ? "material" : "ionicon" },
            tooltip: allSelected ? "Clear all selections" : "Select all options",
            visible: options?.length > 0,
            onPress: () => {
                if (allSelected) {
                    onUpdate([]);
                } else {
                    onUpdate(options?.map(opt => typeof opt === 'object' ? opt.value : opt) ?? []);
                }
            }
        }
    ] : [];

    return(
        <View style={styles.container}>
            {!!title && (
                <View style={styles.labelContainer}>
                    <Text style={styles.labelText}>
                        {title}
                        {required && <Text style={styles.required}>*</Text>}
                        {!!helpText && <InlineHelpIcon text={helpText} />}
                    </Text>
                </View>
            )}

            <View style={styles.inputWrapper(disabled)}>
                <DropdownMenu
                    testID={`SelectInput-${title}`}
                    disabled={disabled}
                    showFocusOutline
                    title={title}
                    options={options_list ?? []}
                    showSearch
                    multiple={multiple}
                    dropdownWidth={Math.max(triggerLayout?.width, 300)}
                    toolBarButtons={toolBarButtons}
                    triggerComponent={<View
                        style={styles.input(multiple && selectedItems.length > 0, disabled, isNotOnSurface)}
                        onLayout={(e) => setTriggerLayout(e.nativeEvent.layout)}
                    >
                            <View style={styles.inputContent}>
                                {!!icon && <Unicon name={icon} size={16} colorVariant="textSecondary" />}
                                {displayText
                                    ? <Text style={styles.text} numberOfLines={1}>{displayText}</Text>
                                    : <Text style={styles.placeholder}>{placeholder}</Text>
                                }
                            </View>
                        </View>
                    }
                />

                <View style={styles.iconContainer} pointerEvents={disabled ? 'none' : 'box-none'}>
                    {showClose
                        ? <Pressable hitSlop={20} disabled={disabled} testID={`SelectInput-${title}-clear`} onPress={() => onUpdate(multiple ? [] : null)}>
                            <Unicon name="close" colorVariant="textSecondary" size={18}/>
                        </Pressable>
                        : <View pointerEvents="none">
                            <Unicon name="chevron-expand-outline" colorVariant="textSecondary" size={18}/>
                        </View>
                    }
                </View>
            </View>

            {/* Pills for multiple selection */}
            {!hideSelectedPills && pills?.length > 0 && (
                <View style={styles.pillsContainer}>
                    {pills}
                </View>
            )}
        </View>
    )
}

const styles = StyleSheet.create(({colors, fonts, shadows, spacing, roundness, typography}, rt) => {
    return {
        container: {
            marginVertical: spacing.sm,
        },
        labelContainer: {
            flex:1,
            flexDirection: "row",
            alignItems: "center",
            paddingBottom: 6,
        },
        labelText: {
            color: colors.textSecondary,
            fontFamily: fonts.heading,
            fontSize: 13,
            flexShrink: 1,
        },
        input: (hasPills, disabled, isNotOnSurface) => ({
            color: colors.text,
            backgroundColor: isNotOnSurface ? colors.surface : colors.inputBackground,
            boxShadow: isNotOnSurface ? shadows.card : shadows.none,
            borderRadius: roundness.sm,
            width: "100%",
            paddingLeft: 14,
            paddingRight: 10,
            outlineStyle: 'none',
            flexDirection: "row",
            justifyContent: "space-between",
            alignItems: "center",
            minHeight: Math.max(44, Math.ceil(44 * rt.fontScale)),
            height: hasPills ? 'auto' : Math.max(44, Math.ceil(44 * rt.fontScale)),
            paddingVertical: hasPills ? 8 : 0,
            outlineWidth: 0,
            cursor: "pointer",
            _web: {
                transition: "all 0.15s ease",
                _focus: {
                    boxShadow: disabled ? "" : shadows.input,
                },
                _hover: {
                    backgroundColor: disabled ? (isNotOnSurface ? colors.surface : colors.inputBackground) : colors.hover,
                }
            }
        }),
        inputContent: {
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 6,
            minWidth: 0,
        },
        options: {
            padding: 15, 
            borderBottomWidth: 0.5, 
            borderBottomColor: colors.border,
        },
        text: {
            color: colors.text,
            fontSize: typography.body,
            fontFamily: fonts.body,
            flexShrink: 1,
            minWidth: 0,
        },
        placeholder: {
            color: colors.textSecondary,
            fontSize: typography.body,
            fontFamily: fonts.body,
        },
        inputWrapper: (disabled) => ({
            position: "relative",
            opacity: disabled ? 0.5 : 1,
        }),
        iconContainer: {
            position: "absolute",
            right: 12,
            top: 0,
            bottom: 0,
            zIndex: 1, 
            flexDirection: "row", 
            alignItems: "center", 
            gap: 6,
        },
        pillsContainer: {
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 6,
            marginTop: spacing.sm,
        },
        pill: (hovered, pillValue) => ({
            backgroundColor: hovered === pillValue ? colors.primary : colors.surfaceVariant,
            paddingHorizontal: 10,
            paddingVertical: 5,
            borderRadius: 8,
            flexDirection: 'row',
            alignItems: 'center',
            gap: 4,
            _web: {
                transition: "all 0.15s ease",
            }
        }),
        pillText: (hovered, pillValue) => ({
            color: hovered === pillValue ? colors.onPrimary : colors.text,
            fontSize: typography.caption,
            fontFamily: fonts.body,
        }),
        required: {
            color: colors.error,
            fontSize: 14,
            fontFamily: fonts.body,
        },
    }
})

export default SelectInput